import AggregateMixin from "csi/action/AggregateMixin";
import { getSpecsForKeys } from "csi/middleware/attributeRule/RuleService";
import Key from "csi/core/Key";
import csiPrefs from "csi/prefs/_base";
import StoreNodesConstructor, { StoreNodes } from "csi/store/Nodes";
import AggregateFrom from "csi/table/AggregateFrom";
import NewMixin from "csi/table/NewMixin";
import xml from "csi/xml";
import declare from "dojo/_base/declare";
import _ from "lodash";
import { EXCLUDE_ITEM_IDS, PP_ATTRIBUTE_SETTING_CONTAINER_URL } from "src/ProductPresentation/PPService";
import PPAttributeTable from "./PPAttributeTable";

const PPAttributeRuleController = declare([AggregateFrom, AggregateMixin, NewMixin], {
	/**
	 * @override
	 * Pre-select nodes that correspond to keys already in the key container
	 */
	onFormData(fd) {
		// select nodes that already exist as keys
		_.forEach(this._newResultUrls, (url) => {
			fd.addAggregatedNode(this.m_cache.getNode(url));
		});
	},

	/**
	 * @override
	 * Save attributes to custom URL
	 */
	_processFormData(fd) {
		const ppTable = this.widget as unknown as typeof PPAttributeTable.prototype;
		const type = ppTable.getNodeTypeOfFilteringAttributes();
		const keyContainerUrl = ppTable.getKeyContainerUrl();

		// get the difference of the union of nodes against removed nodes to consider all nodes selected and de-selected
		const nodes = _.difference(_.union(fd.getSelectedNodes(), fd.getAggregatedNodes()), fd.getRemovedNodes());

		// get key map from selected nodes
		const keyMap: Record<number, string> = {};

		for (const [i, node] of nodes.entries()) {
			keyMap[i] = node["Key"];
		}

		// create node with type as TYPE_VIEW and bChangeIfExists as true
		const cn = this.m_writer
			.createNode(keyContainerUrl, csiPrefs.TYPE_VIEW, null, null, true)
			.changeAttribute("TargetClass", xml.ATTR_STRING, type)
			.changeAttribute("Keys", xml.ATTR_STRINGMAP, keyMap);

		if (!this.m_cache.get(keyContainerUrl)) {
			cn.attach(PP_ATTRIBUTE_SETTING_CONTAINER_URL, "Preferences");
		}

		// process each node selected
		this.urlsAppended = _.map(this._getAggregateSelections(fd), "$URL");

		// process each node removed
		this.urlsRemoved = _.map(this._getAggregateRemovals(fd), "$URL");
	},

	/**
	 * @override
	 * Override the popup params so that we don't need a controller class on AggregateFrom.
	 * Override onCreateMainQueryLevel because this table / view is controlled by this file.
	 */
	onCreatePopupParams(params) {
		params.bResetStore = true;
		const ppTable = this.widget as unknown as typeof PPAttributeTable.prototype;
		const type = ppTable.getNodeTypeOfFilteringAttributes();

		params.onCreateMainQueryLevel = (ql): void => {
			ql.createCustomStore = (): StoreNodes => {
				let attributeKeys = [];

				const productKeys = Key.getKeysForType(type, true);
				const productSpecs = getSpecsForKeys(productKeys);

				for (const spec of productSpecs) {
					const { m_id } = spec.heading;

					if (!spec.isAvailable) {
						continue;
					}

					// these attributes don't make sense here 
					if (EXCLUDE_ITEM_IDS.includes(m_id)) {
						continue;
					}

					// create a fake node to show on the table
					const node = xml.nodeCreateClientNode(spec.getKey());

					// set key on node, make sure it's consistent with the one in the aggregation pop up, so that can 
					// be able to preselect the existing ones
					node.Key = spec.getKey();

					// set name on node
					xml.nodeSetName(node, spec.name);

					// push node to attribute list
					attributeKeys.push(node);
				}

				attributeKeys = xml.sortNodes(attributeKeys);
				return new StoreNodesConstructor({
					// have to specify this to make the find feature work
					bSimpleQueryEngine: true,
					cachingStore: this.widget.cacheView,
					data: attributeKeys,
				});
			};
		};
	},
});

export = PPAttributeRuleController;